import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router";
import { NoBackend } from "@/components/common/NoBackend";
import { PlayPageHeader } from "@/components/grow/PlayPageHeader";
import { gameByKey, groupOfGame } from "@/lib/games/registry";
import type { GameDefinition } from "@/lib/games/types";
import { useGameSession } from "@/lib/games/use-game-session";
import { userErrorMessage } from "@/lib/user-error";

// One game of Grow · Play, chosen by its game key. The page owns the session
// (start, resume, save, finish); what is played belongs to the game itself.
export function GamePage() {
  const intl = useIntl();
  const { gameKey = "" } = useParams();
  const game = gameByKey(gameKey);
  const group = game ? groupOfGame(game.key) : undefined;

  return (
    <section className="mx-auto max-w-screen-md px-4 py-12">
      <PlayPageHeader
        title={game ? intl.formatMessage({ id: game.titleId }) : undefined}
        // In a group the game's own title is short ("Movies"); the group names what it is.
        eyebrow={group ? intl.formatMessage({ id: group.titleId }) : undefined}
        backTo={group ? `/grow/play/games/${group.key}` : undefined}
      />
      {game ? (
        // Keyed by the game, so switching games starts a session of its own.
        <GameSession key={game.key} game={game} />
      ) : (
        <NoBackend
          label={intl.formatMessage({
            id: "grow.games.unknown",
            defaultMessage: "This game is not available here.",
          })}
        />
      )}
    </section>
  );
}

function GameSession({ game }: { game: GameDefinition }) {
  const intl = useIntl();
  const { state, progress, finish, restart } = useGameSession(game);

  if (state.status === "loading") {
    return (
      <p className="text-smycles-body text-ink-muted">
        <FormattedMessage id="common.loading" defaultMessage="Loading…" />
      </p>
    );
  }
  if (state.status === "error") {
    return <p className="text-smycles-body text-ink-muted">{userErrorMessage(intl, state.error)}</p>;
  }
  return (
    // Keyed by the session: playing again mounts the game afresh.
    <game.Component
      key={state.sessionId}
      rules={game.rules}
      target={game.target}
      // The question stands over the rounds, not under the title: said once, where it is answered.
      question={intl.formatMessage({ id: game.descriptionId })}
      snapshot={state.snapshot}
      onProgress={progress}
      onFinish={finish}
      onRestart={restart}
    />
  );
}
